<template>
  <BLayoutView
    :back-route="backRoute"
    :title="pageTitle"
    :skeleton="isLoadingAsyncData"
    :save-menu-list="null"
  >
    <template #form>
      <ComponentForm
        ref="form"
        v-bind="viewTypeOptions"
        :data="data"
        :loading="isLoading"
        :editable="editable"
        :skeleton="isLoadingAsyncData"
        class="my-4"
        @save="onSave"
        @created:publication="onCreatedPublication"
        @updated:publication="onUpdatedPublication"
        @upload-variants="onUploadKimVariants"
        @upload-users="onUploadUsers"
      >
        <template v-if="isEditable" #save-menu>
          <v-row dense class="mt-4 align-center">
            <v-spacer />
            <v-btn
              v-if="!viewTypeOptions.isCreate"
              depressed
              class="mr-6"
              @click="onUpdate"
              v-text="'Обновить'"
            />
            <v-btn
              depressed
              class="mr-6"
              @click="onSave"
              v-text="'Сохранить'"
            />
          </v-row>
        </template>
      </ComponentForm>

      <template v-if="!viewTypeOptions.isCreate && single">
        <PublicationParticipantsTable
          ref="participants"
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />

        <PublicationVariantsTable
          ref="kimVariants"
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />
        <PublicationCriteriaTable
          v-if="single.lk_criteria_storage_id"
          ref="kimCriteria"
          :publication="single"
          :skeleton="isLoadingAsyncData"
          class="my-4"
        />
      </template>
    </template>
  </BLayoutView>
</template>

<script>
import { createViewMixin } from '@/helpers/mixins/createViewMixin'
import { createDeletableMixin } from '@/helpers/mixins/createDeletableMixin'
import { lkPublicationPermissions as Permissions } from '@/helpers/enums/permission-models'
import { PublicationModel as Model } from './Model'
import { collectorPublicationsStoreModule as StoreModule } from './store'
import { COLLECTOR_PUBLICATIONS_ROUTES as ROUTES } from './utils'

import BLayoutView from '@/components/layouts/BLayoutView.vue'
import ComponentForm from './components/PublicationsForm.vue'
import * as VIEW_TYPES from '@/helpers/enums/view-types'

import { CollectorKimsTasksApi } from '@/api/resources/Collector/CollectorKimsTasksApi'

const PublicationParticipantsTable = () => import(
  /* webpackChunkName: "PublicationsParticipantsTable" */
  './Participants/PublicationsParticipantsExpansionTable'
)

const PublicationVariantsTable = () => import(
  /* webpackChunkName: "PublicationsVariantsTable" */
  './Variants/PublicationsVariantsExpansionTable'
)

const PublicationCriteriaTable = () => import(
  /* webpackChunkName: "PublicationsCriteriaTable" */
  './Criteria/PublicationsCriteriaExpansionTable'
)
const mixinOptions = { Model, StoreModule, ROUTES }
const viewMixin = createViewMixin(mixinOptions)
const deletableMixin = createDeletableMixin(mixinOptions)

/** @typedef { import('@/helpers/mixins/createViewMixin').ViewMixin<any, StoreModule> } View */
/** @typedef { import('@/helpers/mixins/createDeletableMixin').DeletableMixin<any, StoreModule> } Deletable */

export default {
  name: 'ViewCollectorPublicationsView',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  components: {
    BLayoutView,
    ComponentForm,
    PublicationVariantsTable,
    PublicationCriteriaTable,
    PublicationParticipantsTable,
  },
  mixins: [viewMixin, deletableMixin],
  data: () => ({
    backRoute: { name: ROUTES.LIST },
    isLoadingVariants: true,
  }),
  computed: {
    viewTypeOptions() {
      const { viewType } = this
      return !this.$access(Permissions.edit)
        ? {
          isCreate: false,
          isEdit: false,
          isView: true,
        }
        : {
          isCreate: viewType === VIEW_TYPES.CREATE,
          isEdit: viewType === VIEW_TYPES.EDIT,
          isView: viewType === VIEW_TYPES.VIEW,
        }
    },
    isEditable() {
      return this.$access(Permissions.edit)
    },
    createRoute() {
      return {
        name: ROUTES.CREATE,
      }
    },
    editRoute() {
      return {
        name: ROUTES.EDIT,
        props: { id: this.id },
      }
    },
    data() {
      return this.viewTypeOptions.isCreate ? void 0 : this.single
    },
  },
  created() {
    this.api = new CollectorKimsTasksApi()
  },
  methods: {
    ...StoreModule.mapMutations(['setOne']),
    async onSave() {
      await this.$refs.form.onSave()
    },
    onUploadKimVariants(value) {
      this.$refs.kimVariants.isLoading = value
    },
    onUploadUsers(value) {
      this.$refs.participants.isLoading = value
    },
    onCreatedPublication(data) {
      this.setOne(data)
      const location = {
        name: ROUTES.EDIT,
        params: { id: data.publication_id },
      }
      this.$router.push(location)
    },
    onUpdatedPublication(data) {
      this.setOne(data)
    },
    onUpdate() {
      this.$refs.form.updatePublication()
      this.$refs.kimVariants.asyncData()
      if (this.$refs.kimCriteria) this.$refs.kimCriteria.asyncData()
      this.$refs.participants.asyncData()
    },
  },
}
</script>
